import React, { useEffect, useState } from "react";
import { Button } from "react-bootstrap";
import { toast } from "react-toastify";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { Loading } from "../../Common/OtherElements/Loading";
import ComponentHeader from "../../Common/OtherElements/ComponentHeader";
import { usePageLevelAccess } from "../../../hooks/usePageLevelAccess";
import { useNavigate } from "react-router-dom";

import { handleErrors } from "../../../utils/errorHandler";

import {
  fetchSubCategoriesForReOrder,
  updateGalleryOrderService,
} from "../../../services/subCategoryService";
import allImages from "../../../assets/images-import";

const ReArrangeSubCategory = ({ show, onHide, productName, onReset }) => {
  const [loading, setLoading] = useState(true);
  const [reorderLoading, setReorderLoading] = useState(false);
  const [manageCategory, setManageCategory] = useState([]);
  const navigate = useNavigate();
  const PageLevelAccessurl = "sub-category";
  const { pageAccessData } = usePageLevelAccess(PageLevelAccessurl);

  // ✅ Fix page access check with proper dependencies
  useEffect(() => {
    if (pageAccessData) {
      if (!pageAccessData.addAccess || !pageAccessData.viewAccess) {
        navigate("/404-error-page");
      }
    }
  }, [pageAccessData, navigate]);

  // React Beautiful DND handler
  const handleDragEnd = async (result) => {
    // If dropped outside the list or no destination
    if (!result.destination) {
      return;
    }

    // If dropped in the same position
    if (result.destination.index === result.source.index) {
      return;
    }
    // Create new array with reordered items
    const newGallery = Array.from(manageCategory);
    const [reorderedItem] = newGallery.splice(result.source.index, 1);
    newGallery.splice(result.destination.index, 0, reorderedItem);

    // Update local state immediately for better UX
    setManageCategory(newGallery);
  };

  const handleUpdateDisplayOrder = async () => {
    try {
      setReorderLoading(true);
      // Prepare data for API call
      const updateOrderData = manageCategory.map((item, index) => ({
        id: item.id,
        displayOrder: index + 1,
      }));

      // Call API to update order
      const response = await updateGalleryOrderService(updateOrderData);

      if (response.isSuccess) {
        toast.success("Sub Category order updated successfully!");
      } else {
        throw new Error("Failed to update gallery order");
      }
    } catch (error) {
      handleErrors(error);
      // Revert local changes on error
      fetchGalleryImages();
    } finally {
      setReorderLoading(false);
    }
  };

  const fetchGalleryImages = async () => {
    try {
      setLoading(true);
      const response = await fetchSubCategoriesForReOrder();
      if (response.isSuccess) {
        // Sort by displayOrder if available, otherwise maintain original order
        const sortedGallery = response.result.sort((a, b) => {
          if (a.displayOrder && b.displayOrder) {
            return a.displayOrder - b.displayOrder;
          }
          return 0;
        });
        setManageCategory(sortedGallery);
      }
    } catch (error) {
      handleErrors(error);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchGalleryImages();
  }, []);

  return (
    <>
      <ComponentHeader title="Re-Arrange Sub Category" />
      <div className="row">
        <div className="col-lg-12">
          {loading ? (
            <Loading />
          ) : (
            <>
              <div className=" text-end mb-3">
                {!reorderLoading && (
                  <Button
                    variant="secondary"
                    onClick={handleUpdateDisplayOrder}
                    disabled={reorderLoading}
                    className="ms-5"
                  >
                    Update Display Order
                  </Button>
                )}
                {reorderLoading && (
                  <span className="ms-2 ">
                    <small className="text-primary fs-16">
                      <span
                        className="spinner-border spinner-border-sm me-1"
                        role="status"
                      ></span>
                      Updating order...
                    </small>
                  </span>
                )}
              </div>
              {manageCategory.length === 0 ? (
                <div className="text-center py-4">
                  <p className="text-muted">No images found for this product</p>
                </div>
              ) : (
                <>
                  <DragDropContext onDragEnd={handleDragEnd}>
                    <Droppable
                      droppableId="gallery-items"
                      direction="horizontal"
                    >
                      {(provided, snapshot) => (
                        <div
                          className="row"
                          {...provided.droppableProps}
                          ref={provided.innerRef}
                          style={{
                            minHeight: "200px",
                            backgroundColor: snapshot.isDraggingOver
                              ? "rgba(13, 110, 253, 0.05)"
                              : "transparent",
                            borderRadius: "8px",
                            transition: "background-color 0.2s ease",
                            padding: "10px",
                          }}
                        >
                          {manageCategory.map((item, index) => (
                            <Draggable
                              key={item.id.toString()}
                              draggableId={item.id.toString()}
                              index={index}
                              isDragDisabled={reorderLoading}
                            >
                              {(provided, snapshot) => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  className="col-md-3 col-sm-6 mb-3"
                                  style={{
                                    ...provided.draggableProps.style,
                                    transform: snapshot.isDragging
                                      ? provided.draggableProps.style?.transform
                                      : "none",
                                  }}
                                >
                                  <div
                                    className={`card h-100 ${
                                      snapshot.isDragging
                                        ? "shadow-lg border-primary"
                                        : "shadow-sm"
                                    }`}
                                    style={{
                                      transition: snapshot.isDragging
                                        ? "none"
                                        : "all 0.2s ease",
                                      transform: snapshot.isDragging
                                        ? "rotate(5deg)"
                                        : "rotate(0deg)",
                                      opacity: snapshot.isDragging ? 0.9 : 1,
                                    }}
                                  >
                                    <div className="position-relative">
                                      {/* Drag Handle */}
                                      <div
                                        {...provided.dragHandleProps}
                                        className="position-absolute bg-primary text-white d-flex align-items-center justify-content-center"
                                        style={{
                                          top: "8px",
                                          left: "8px",
                                          width: "32px",
                                          height: "32px",
                                          borderRadius: "6px",
                                          cursor: reorderLoading
                                            ? "not-allowed"
                                            : "grab",
                                          zIndex: 2,
                                          fontSize: "14px",
                                          fontWeight: "bold",
                                          boxShadow:
                                            "0 2px 4px rgba(0,0,0,0.2)",
                                        }}
                                        title="Drag to reorder"
                                      >
                                        {index + 1}
                                      </div>

                                      {/* Drag Handle Icon */}
                                      <div
                                        className="position-absolute text-white"
                                        style={{
                                          top: "45px",
                                          left: "12px",
                                          fontSize: "16px",
                                          zIndex: 2,
                                          textShadow:
                                            "1px 1px 2px rgba(0,0,0,0.7)",
                                          pointerEvents: "none",
                                        }}
                                      >
                                        ⋮⋮
                                      </div>

                                      <img
                                        src={item.imageURL}
                                        alt={`Product ${index + 1}`}
                                        className="card-img-top"
                                        style={{
                                          width: "100%",
                                          height: "160px", // Reduced to make room for category name
                                          objectFit: "cover",
                                          userSelect: "none",
                                          borderRadius:
                                            "calc(0.375rem - 1px) calc(0.375rem - 1px) 0 0",
                                        }}
                                        onError={(e) => {
                                          e.target.src =
                                            allImages.ImagePlaceholder;
                                        }}
                                        onDragStart={(e) => e.preventDefault()}
                                      />

                                      {/* Overlay for dragging state */}
                                      {snapshot.isDragging && (
                                        <div
                                          className="position-absolute w-100 h-100 d-flex align-items-center justify-content-center"
                                          style={{
                                            top: 0,
                                            left: 0,
                                            backgroundColor:
                                              "rgba(13, 110, 253, 0.1)",
                                            borderRadius:
                                              "calc(0.375rem - 1px)",
                                            zIndex: 1,
                                          }}
                                        >
                                          <div className="text-primary fw-bold">
                                            Moving...
                                          </div>
                                        </div>
                                      )}
                                    </div>

                                    {/* Category Name Card Body */}
                                    <div className="card-body p-2 d-flex align-items-center justify-content-center p-2">
                                      <h6
                                        className="card-title mb-1 text-center text-truncate fw-bold"
                                        style={{ fontSize: "14px" }}
                                        title={item.subCategoryName}
                                      >
                                        {item.subCategoryName}
                                      </h6>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </DragDropContext>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default ReArrangeSubCategory;
